import { useEffect } from 'react'

type AcceptableKeys =
  | 'Escape'
  | 'Enter'
  | 'Tab'
  | 'ArrowUp'
  | 'ArrowDown'
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'Shift'
  | 'Control'

/**
 * hook that captures keyboard shortcuts and calls injected handlers
 * @note two of these hooks can be used to capture to key events, (like cmd + s)
 * @see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key
 */
export const useKey = (
  key: AcceptableKeys,
  onKeyDown: (event: KeyboardEvent) => void,
  onKeyUp?: (event: KeyboardEvent) => void,
) => {
  useEffect(() => {
    const keyDownCallback = (event: KeyboardEvent) => {
      if (event.key === key) {
        return onKeyDown(event)
      }
    }

    const keyUpCallback = (event: KeyboardEvent) => {
      if (event.key === key && onKeyUp) {
        return onKeyUp(event)
      }
    }
    document.addEventListener('keydown', keyDownCallback)
    if (onKeyUp) {
      document.addEventListener('keyup', keyUpCallback)
    }

    return () => {
      document.removeEventListener('keydown', keyDownCallback)

      if (onKeyUp) {
        document.removeEventListener('keyup', keyUpCallback)
      }
    }
  }, [key, onKeyDown])
}
